import { FlaskConical, Pill, Package, FileText, AlertCircle } from "lucide-react";

const SERVICES = [
    {
        icon: FlaskConical,
        title: "Lab Specimens",
        description:
            "Temperature-controlled, chain-of-custody transport for blood draws, biopsies, cultures, and lab samples.",
        image:
            "https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&q=70&w=900",
        span: "md:col-span-7 md:row-span-2",
    },
    {
        icon: Pill,
        title: "Pharmaceuticals",
        description:
            "Secure medication and prescription delivery to clinics, long-term care, and patients.",
        span: "md:col-span-5",
    },
    {
        icon: Package,
        title: "Medical Supplies",
        description:
            "Reliable transport of supplies, equipment, and durable medical goods between facilities.",
        image:
            "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&q=70&w=700",
        span: "md:col-span-5",
    },
    {
        icon: FileText,
        title: "Medical Documents",
        description:
            "HIPAA-aware handling of charts, records, and sensitive paperwork across facilities.",
        span: "md:col-span-4",
    },
    {
        icon: AlertCircle,
        title: "STAT Rush",
        description:
            "Critical, time-sensitive runs dispatched immediately when minutes matter most.",
        span: "md:col-span-3",
        highlight: true,
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="py-24 md:py-32 bg-brand-black text-white relative overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
                <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-brand-gold blur-3xl" />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-12 mb-16">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-px bg-brand-gold" />
                            <span className="text-[10px] tracking-[0.4em] uppercase text-brand-gold">
                                Services
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">
                            Every package, treated like a patient.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:pt-6">
                        <p className="text-lg text-white/70 leading-relaxed">
                            From routine pickups to STAT urgent runs, our medical courier
                            services are designed around the realities of healthcare —
                            chain-of-custody documentation, temperature awareness, and the
                            kind of dependability that lets clinical teams focus on what they
                            do best.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-12 gap-4 auto-rows-[280px]">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.title}
                                data-testid={`service-card-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                                className={`group relative overflow-hidden border ${
                                    s.highlight
                                        ? "border-brand-gold bg-brand-gold/10"
                                        : "border-white/10 bg-white/[0.02]"
                                } ${s.span} hover:border-brand-gold transition-all duration-500`}
                            >
                                {s.image && (
                                    <>
                                        <img
                                            src={s.image}
                                            alt={`${s.title} medical courier service`}
                                            loading="lazy"
                                            decoding="async"
                                            className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/70 to-transparent" />
                                    </>
                                )}
                                <div className="relative h-full p-7 flex flex-col justify-between">
                                    <Icon
                                        size={28}
                                        strokeWidth={1.25}
                                        className={s.highlight ? "text-brand-gold" : "text-brand-gold"}
                                    />
                                    <div>
                                        <div className="font-serif text-2xl md:text-3xl mb-2">
                                            {s.title}
                                        </div>
                                        <div className="text-sm text-white/65 leading-relaxed max-w-md">
                                            {s.description}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
